import React, { useEffect, useRef } from 'react';
import { InterestRatesHistoricalSliderItem } from './types';
import useMediaQuery from '../../../hooks/use-media';
import { mainMediaTablet } from '../../../utils';

interface InterestRatesHistoricalSliderTimelineProps {
  activeSlideIndex: number;
  currentSlideIndex: number;
  sliderItems: InterestRatesHistoricalSliderItem[];
  onTimelineItemClick: (index: number) => void;
}

const InterestRatesHistoricalSliderTimeline: React.FC<
  InterestRatesHistoricalSliderTimelineProps
> = ({ sliderItems, activeSlideIndex, currentSlideIndex, onTimelineItemClick }) => {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the container
  const isMobile = useMediaQuery(mainMediaTablet);

  useEffect(() => {
    if (!isMobile || !containerRef.current) {
      return;
    }

    const activeItem = itemRefs.current[activeSlideIndex];
    if (activeItem) {
      // Use the containerRef to limit scrolling to the container
      const container = containerRef.current;
      const itemLeft = activeItem.offsetLeft; // Get the position of the active item
      const itemWidth = activeItem.offsetWidth;
      const containerWidth = container.offsetWidth;

      // Calculate the scroll position to center the active item
      const scrollPosition = itemLeft - containerWidth / 2 + itemWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [activeSlideIndex]);

  return (
    <div ref={containerRef} className="w-full overflow-hidden">
      <div className="flex items-center">
        {sliderItems.map((item, index) => (
          <React.Fragment key={index}>
            <div
              ref={(el) => (itemRefs.current[index] = el)}
              onClick={() => onTimelineItemClick(index)}
              className="flex flex-col items-center min-w-[45px] cursor-pointer">
              <div className="relative flex items-center justify-center w-8 h-8 rounded-full border-2 border-black border-solid">
                {activeSlideIndex === index && (
                  <div className="w-4 h-4 bg-blue-LINK rounded-full"></div>
                )}
              </div>
              <div className={`mt-4 ${activeSlideIndex === index && 'font-bold'}`}>{item.year}</div>
            </div>
            {index < sliderItems.length - 1 && (
              <div className="h-14 min-w-[25px] relative border-transparent border-t-black border-t-2 border-dotted flex-grow">
                {item.content.length > 1 &&
                  item.content.map((_, i) => (
                    <div
                      key={i}
                      style={{ left: `${_.position}%` }}
                      className="absolute h-full -translate-y-[6px] invisible md:visible">
                      <div
                        className={`w-4 h-4 ${
                          activeSlideIndex + index + i === currentSlideIndex + 1 &&
                          activeSlideIndex === index
                            ? 'bg-gray-500'
                            : 'bg-white'
                        } border border-solid border-black rounded-full`}></div>
                    </div>
                  ))}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default InterestRatesHistoricalSliderTimeline;
